/**
 * DID Numbers Composable - Manages state and operations for DID numbers
 */

import { ref, computed, readonly } from 'vue';
import { didService, type DidNumber, type DidNumberFilters, type DidNumberResponse, type DidNumberCreateData, type DidNumberUpdateData, DidApiError } from '@/services/didService';
import { useNotificationStore } from '@/stores/notification';
import { apiClient } from '@/services/apiClient';

interface DidNumberState {
  numbers: readonly DidNumber[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
  exporting: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
}

export function useDidNumbers() {
  const notificationStore = useNotificationStore();
  
  const state = ref<DidNumberState>({
    numbers: [],
    loading: false,
    error: null,
    currentPage: 1,
    itemsPerPage: 25,
    totalItems: 0,
    totalPages: 0,
    exporting: false,
    creating: false,
    updating: false,
    deleting: false
  });

  const filters = ref<DidNumberFilters>({
    search: '',
    dateRange: { value: null },
    smsEnabled: { value: null },
    channelRange: { value: null }
  });

  // Computed properties
  const hasError = computed(() => !!state.value.error);
  const hasData = computed(() => state.value.numbers?.length > 0 || false);
  const isEmpty = computed(() => !state.value.numbers?.length);
  
  // Pagination computed properties
  const hasNextPage = computed(() => state.value.currentPage < state.value.totalPages);
  const hasPreviousPage = computed(() => state.value.currentPage > 1);
  const canGoToNextPage = computed(() => hasNextPage.value && state.value.totalItems > 0);
  const canGoToPreviousPage = computed(() => hasPreviousPage.value && state.value.totalItems > 0);

  // Methods
  const fetchDidNumbers = async () => {
    state.value.loading = true;
    state.value.error = null;
    
    try {
      const filterParams: DidNumberFilters = {
        ...filters.value,
        page: state.value.currentPage,
        pageSize: state.value.itemsPerPage
      };

      const response = await didService.getDidNumbers(filterParams);

      state.value.numbers = Array.isArray(response.data) ? response.data : [];
      state.value.totalItems = response.meta?.pagination?.total || 0;
      state.value.totalPages = response.meta?.pagination?.total_pages || 0;
      state.value.currentPage = response.meta?.pagination?.page || 1;
      
    } catch (error) {
      console.error('Error fetching DID numbers:', error);
      
      state.value.numbers = [];
      state.value.totalItems = 0;
      state.value.totalPages = 0;
      
      if (error instanceof DidApiError) {
        state.value.error = error.message;
        
        // Show specific error notifications
        if (error.code === 'NETWORK_ERROR') {
          notificationStore.showError({
            title: 'Network Error',
            text: 'Unable to connect to the server. Please check your connection.'
          });
        } else if (error.code === 'VALIDATION_ERROR') {
          notificationStore.showError({
            title: 'Invalid Request',
            text: error.message
          });
        } else {
          notificationStore.showError({
            title: 'Error',
            text: error.message
          });
        }
      } else {
        state.value.error = 'An unexpected error occurred';
        notificationStore.showError({
          title: 'Error',
          text: 'An unexpected error occurred while fetching DID numbers'
        });
      }
    } finally {
      state.value.loading = false;
    }
  };

  const retry = () => {
    fetchDidNumbers();
  };

  const changePage = (page: number) => {
    state.value.currentPage = page;
    fetchDidNumbers();
  };

  const changeItemsPerPage = (itemsPerPage: number) => {
    state.value.itemsPerPage = itemsPerPage;
    state.value.currentPage = 1;
    fetchDidNumbers();
  };

  const updateSearch = (search: string) => {
    filters.value.search = search;
    state.value.currentPage = 1;
    fetchDidNumbers();
  };

  const updateFilters = (newFilters: Partial<DidNumberFilters>) => {
    Object.assign(filters.value, newFilters);
    state.value.currentPage = 1;
    fetchDidNumbers();
  };

  const resetFilters = () => {
    filters.value = {
      search: '',
      dateRange: { value: null },
      smsEnabled: { value: null },
      channelRange: { value: null }
    };
    state.value.currentPage = 1;
    fetchDidNumbers();
  };

  const clearError = () => {
    state.value.error = null;
  };

  const exportDidNumbers = async () => {
    state.value.exporting = true;
    
    try {
      await didService.exportDidNumbers(filters.value);
      
      notificationStore.showSuccess({
        title: 'Export Started',
        text: 'Your DID numbers export has been downloaded.'
      });
      
    } catch (error) {
      console.error('Error exporting DID numbers:', error);
      
      if (error instanceof DidApiError) {
        notificationStore.showError({
          title: 'Export Failed',
          text: error.message
        });
      } else {
        notificationStore.showError({
          title: 'Export Failed',
          text: 'Failed to export DID numbers. Please try again.'
        });
      }
    } finally {
      state.value.exporting = false;
    }
  };

  const initDidNumberCreation = async (): Promise<string | null> => {
    try {
      const response = await didService.initDidNumberCreation();
      return response.number;
    } catch (error) {
      console.error('Error initializing DID number creation:', error);
      
      if (error instanceof DidApiError) {
        notificationStore.showError({
          title: 'Initialization Failed',
          text: error.message
        });
      } else {
        notificationStore.showError({
          title: 'Initialization Failed',
          text: 'Failed to initialize DID number creation. Please try again.'
        });
      }
      
      return null;
    }
  };

  const createDidNumber = async (data: DidNumberCreateData): Promise<void> => {
    state.value.creating = true;
    
    try {
      await didService.createDidNumber(data);
      
      notificationStore.showSuccess({
        title: 'DID Number Created',
        text: `DID number ${data.number} has been created successfully.`
      });
      
      // Refresh the list
      await fetchDidNumbers();
      
    } catch (error) {
      console.error('Error creating DID number:', error);
      
      if (error instanceof DidApiError) {
        notificationStore.showError({
          title: 'Creation Failed',
          text: error.message
        });
      } else {
        notificationStore.showError({
          title: 'Creation Failed',
          text: 'Failed to create DID number. Please try again.'
        });
      }
    } finally {
      state.value.creating = false;
    }
  };

  const updateDidNumber = async (id: number, data: DidNumberUpdateData): Promise<void> => {
    state.value.updating = true;
    
    try {
      await didService.updateDidNumber(id, data);
      
      notificationStore.showSuccess({
        title: 'DID Number Updated',
        text: 'DID number has been updated successfully.'
      });
      
      // Refresh the list
      await fetchDidNumbers();
      
    } catch (error) {
      console.error('Error updating DID number:', error);
      
      if (error instanceof DidApiError) {
        notificationStore.showError({
          title: 'Update Failed',
          text: error.message
        });
      } else {
        notificationStore.showError({
          title: 'Update Failed',
          text: 'Failed to update DID number. Please try again.'
        });
      }
    } finally {
      state.value.updating = false;
    }
  };

  const deleteDidNumber = async (id: number, number: string): Promise<void> => {
    state.value.deleting = true;
    
    try {
      await didService.deleteDidNumber(id);
      
      notificationStore.showSuccess({
        title: 'DID Number Deleted',
        text: `DID number ${number} has been deleted successfully.`
      });
      
      // Refresh the list
      await fetchDidNumbers();
      
    } catch (error) {
      console.error('Error deleting DID number:', error);
      
      if (error instanceof DidApiError) {
        notificationStore.showError({
          title: 'Deletion Failed',
          text: error.message
        });
      } else {
        notificationStore.showError({
          title: 'Deletion Failed',
          text: 'Failed to delete DID number. Please try again.'
        });
      }
    } finally {
      state.value.deleting = false;
    }
  };

  const getDidNumberById = async (id: number): Promise<DidNumber | null> => {
    try {
      return await didService.getDidNumberById(id);
    } catch (error) {
      console.error('Error fetching DID number details:', error);
      
      if (error instanceof DidApiError) {
        notificationStore.showError({
          title: 'Error Loading DID Number',
          text: error.message
        });
      } else {
        notificationStore.showError({
          title: 'Error',
          text: 'Failed to load DID number details. Please try again.'
        });
      }
      
      return null;
    }
  };

  // Helper methods for display
  const getSmsStatusColor = (isSmsEnabled: boolean): string => {
    return didService.getSmsStatusColor(isSmsEnabled);
  };

  const getSmsStatusDisplay = (isSmsEnabled: boolean): string => {
    return didService.getSmsStatusDisplay(isSmsEnabled);
  };

  const getQuarantineStatusColor = (isQuarantined: boolean): string => {
    return didService.getQuarantineStatusColor(isQuarantined);
  };

  const getQuarantineStatusDisplay = (isQuarantined: boolean): string => {
    return didService.getQuarantineStatusDisplay(isQuarantined);
  };

  return {
    // State (readonly to prevent direct mutation)
    state: readonly(state),
    numbers: computed(() => state.value.numbers),
    loading: computed(() => state.value.loading),
    error: computed(() => state.value.error),
    currentPage: computed(() => state.value.currentPage),
    itemsPerPage: computed(() => state.value.itemsPerPage),
    totalItems: computed(() => state.value.totalItems),
    totalPages: computed(() => state.value.totalPages),
    exporting: computed(() => state.value.exporting),
    creating: computed(() => state.value.creating),
    updating: computed(() => state.value.updating),
    deleting: computed(() => state.value.deleting),
    
    // Computed properties
    hasError,
    hasData,
    isEmpty,
    hasNextPage,
    hasPreviousPage,
    canGoToNextPage,
    canGoToPreviousPage,
    
    // Filters
    filters: readonly(filters),
    
    // Actions
    fetchDidNumbers,
    retry,
    changePage,
    changeItemsPerPage,
    updateSearch,
    updateFilters,
    resetFilters,
    clearError,
    exportDidNumbers,
    initDidNumberCreation,
    createDidNumber,
    updateDidNumber,
    deleteDidNumber,
    getDidNumberById,
    
    // Helper methods
    getSmsStatusColor,
    getSmsStatusDisplay,
    getQuarantineStatusColor,
    getQuarantineStatusDisplay
  };
} 